/**
 * The quick look's size when the reader drags its corner (the owner,
 * 2026-10-15: "on the popup chart, is way i can resize it?", then "yes, add
 * it and deploy it").
 *
 * The window is centred, so a drag grows it by twice the pointer's move: half
 * on each side, the corner staying under the pointer. Never smaller than
 * `QUICK_LOOK_MIN`, never larger than the screen less a margin. The size is
 * kept on this machine (`QUICK_LOOK_SIZE_KEY`, a layout key: screen geometry,
 * never synced, one per Display Profile), and a double-click on the corner
 * forgets it, back to the starting size.
 *
 * Pure: the window hands in the numbers. `npm run outlookpreview:check`.
 */

export const QUICK_LOOK_SIZE_KEY = "mpp:chart-quick-look:size:v1";

export type QuickLookSize = { w: number; h: number };

/** The smallest the window is drawn: the chart's toolbars and a plot still fit. */
export const QUICK_LOOK_MIN: QuickLookSize = { w: 420, h: 300 };

/** Room kept between the window and the screen's edge, each side, in pixels. */
export const QUICK_LOOK_MARGIN = 8;

/** A size within the minimum and the screen, whole pixels. */
export function clampQuickLook(size: QuickLookSize, screen: QuickLookSize): QuickLookSize {
  const maxW = Math.max(QUICK_LOOK_MIN.w, screen.w - 2 * QUICK_LOOK_MARGIN);
  const maxH = Math.max(QUICK_LOOK_MIN.h, screen.h - 2 * QUICK_LOOK_MARGIN);
  return {
    w: Math.round(Math.min(maxW, Math.max(QUICK_LOOK_MIN.w, size.w))),
    h: Math.round(Math.min(maxH, Math.max(QUICK_LOOK_MIN.h, size.h))),
  };
}

/** The size after the corner moved `dx`, `dy` from where the drag began. */
export function resizedQuickLook(start: QuickLookSize, dx: number, dy: number, screen: QuickLookSize): QuickLookSize {
  return clampQuickLook({ w: start.w + 2 * dx, h: start.h + 2 * dy }, screen);
}

/** A kept size, or null when nothing usable is kept. */
export function parseQuickLookSize(raw: string | null): QuickLookSize | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { w?: unknown; h?: unknown };
    const w = Number(v?.w);
    const h = Number(v?.h);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < QUICK_LOOK_MIN.w || h < QUICK_LOOK_MIN.h) return null;
    return { w: Math.round(w), h: Math.round(h) };
  } catch {
    return null;
  }
}
